import React, { useEffect, useState } from 'react';
import styles from '../style/styles/Form.module.css';
import '../style/styles/global.scss';
import { useChatStateStore, useFormStore, useMessagesStore, useUserStore } from '../zustand/store';
import { useFormSubmitted, useFormSubmittedFastfive } from '../services/queries/chatbotQueries';
import { getFormConfig } from '../data/formConfig';
import { useFormValidation, FormField, CheckboxGroup } from './Form/FormUtils';
import { openCenteredWindow } from '../utils/utils';

export const CollectionForm = (props) => {
    const { partnerType, partnerId } = useUserStore();
    const {
        chatContainerRef,
    } = props;

    const { setIsFormSubmitted } = useChatStateStore();
    const { setMessages } = useMessagesStore();
    const { preAnswersObject, clearPreAnswersObject } = useFormStore();
    const { mutate: formSubmitted } = useFormSubmitted();
    const { mutate: formSubmittedFastfive } = useFormSubmittedFastfive();
    // Get form configuration based on clientId
    const formConfig = getFormConfig(partnerId);
    
    // Use custom hook for form validation
    const { formData, validList, allValid, handleInputChange, handleCheckboxChange, setFormData } = useFormValidation(formConfig);
    
    const [currentFormStep, setCurrentFormStep] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isFormStarted, setIsFormStarted] = useState(formConfig.triggerType === 'auto');
    
    // Handle form start button click
    const handleFormStart = () => {
        setIsFormStarted(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const isLastStep = currentFormStep === formConfig.fields.length - 1;
        if (!isLastStep) {
            return; // Prevent implicit submit on intermediate steps
        }
        if (partnerType === 'fastfive') {
            formSubmittedFastfive({
                formData: formData,
            });
        } else {
            formSubmitted({
                formData: formData,
            });
        }
        setMessages([{message: formData, type: 'form', isChatbot: false}]);
        setIsSubmitted(true);
        setIsFormSubmitted('submitted');
        const targetWindow = window.parent;
        const formSubmittedState = {
            formSubmittedState: true,
            firstAnswer: preAnswersObject['0'],
            secondAnswer: preAnswersObject['1'],
            log: 'formSubmittedState in handleSubmit'
        }
        clearPreAnswersObject();
        targetWindow.postMessage(formSubmittedState, "*");
    };  

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatContainerRef, currentFormStep])

    // Render form trigger button if needed
    const renderFormTrigger = () => {
        if (formConfig.triggerType === 'button' && !isFormStarted) {
            return (
                <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <button 
                        type="button"
                        className={!isFormStarted ? styles.CollectionForm__button__started : styles.CollectionForm__button}
                        onClick={(e) => { e.stopPropagation(); handleFormStart(); }}
                    >
                        {formConfig.triggerButtonText}
                    </button>
                    {formConfig.supportiveType.tel && (
                        <button 
                            type="button"
                            style={{ backgroundColor: '#e9e9e9', border: 'none', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${formConfig.supportiveUrl.tel}`; }}
                        >
                            <img src='/img/units/tel-cs.png' alt="tel" width={16} height={16} />
                        </button>
                    )}
                    {formConfig.supportiveType.kakao && (
                        <button 
                            type="button"
                            style={{ backgroundColor: '#e9e9e9', border: 'none', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                            onClick={(e) => { e.stopPropagation(); openCenteredWindow(formConfig.supportiveUrl.kakao, 'kakaoChat', 480, 640); }}
                        >
                            <img src='/img/units/kakaotalk-icon.png' alt="kakao" width={38} height={38} />
                        </button>
                    )}
                </div>
            );
        }
        return null;
    };

    const handleFormKeyDown = (e) => {
        if (e.key === 'Enter') {
            const isLastStep = currentFormStep === formConfig.fields.length - 1;
            if (!isLastStep) {
                e.preventDefault();
                const currentField = formConfig.fields[currentFormStep];
                const isCurrentFieldValid = validList[currentField?.id] || false;
                if (isCurrentFieldValid && !isSubmitted) {
                    setCurrentFormStep(currentFormStep + 1);
                }
            }
        }
    };

    // Render form fields based on current step
    const renderFormFields = () => {
        if (!isFormStarted && formConfig.triggerType === 'button') {
            return null;
        }

        const currentField = formConfig.fields[currentFormStep];
        const isLastStep = currentFormStep === formConfig.fields.length - 1;
        const isCurrentFieldValid = validList[currentField?.id] || false;

        return (
            <>
                {currentField && (
                    <FormField
                        field={currentField}
                        formData={formData}
                        validList={validList}
                        onInputChange={handleInputChange}
                        disabled={isSubmitted}
                    />
                )}
                
                {!isLastStep && (
                    <button 
                        type="button"
                        className={isCurrentFieldValid && !isSubmitted ? styles.CollectionForm__button__small : styles.CollectionForm__button__small__disabled}
                        disabled={isSubmitted || !isCurrentFieldValid}
                        onClick={(e) => { e.stopPropagation(); setCurrentFormStep(currentFormStep + 1); }}
                    >
                        다음
                    </button>
                )}

                {isLastStep && (
                    <>
                        <CheckboxGroup
                            checkboxGroup={formConfig.checkboxGroup}
                            formData={formData}
                            validList={validList}
                            onCheckboxChange={handleCheckboxChange}
                            disabled={isSubmitted}
                        />
                        {!isSubmitted && (
                            <button 
                                type="submit" 
                                className={allValid && !isSubmitted ? styles.CollectionForm__button : styles.CollectionForm__button__disabled}
                                disabled={isSubmitted || !allValid}
                                onClick={(e) => { e.stopPropagation(); }}
                            >
                                {formConfig.submitButton.text}
                            </button>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <div className={styles.CollectionForm__container}>
            <div 
                className={!isFormStarted ? styles.CollectionForm__wrapper__started : styles.CollectionForm__wrapper}
            >
                <form 
                    onSubmit={handleSubmit}
                    onKeyDown={handleFormKeyDown}
                    style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {renderFormTrigger()}
                    {renderFormFields()}
                </form>
            </div>
        </div>
    );
};

export const CompletedForm = (props) => {
    const { partnerType, partnerId } = useUserStore();
    const { 
        formData, 
        chatContainerRef,
        // clientId = partnerType === 'fastfive' ? 'fastfive' : 'csInquiry' 
     } = props;
    const formConfig = getFormConfig(partnerId);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatContainerRef])

    return (
        <div className={styles.CollectionForm__container}>
            <div className={styles.CollectionForm__wrapper}>
                <form>
                    {formConfig.fields.map(field => (
                        <div key={field.id} className={styles.CollectionForm__group}>
                            <label className={`${styles.CollectionForm__label} ${field.required ? styles.CollectionForm__required : ''}`}>
                                {field.label}
                            </label>
                            <input
                                type={field.type}
                                name={field.id}
                                className={styles.CollectionForm__input}
                                value={formData[field.id] || ''}
                                disabled={true}
                                autoComplete="off"
                            />
                        </div>
                    ))}

                    {formConfig.checkboxGroup.enabled && (
                        <div className={styles.CollectionForm__checkboxGroup}>
                            <label className={styles.CollectionForm__checkboxLabel}>
                                <input
                                    type="checkbox"
                                    name="all"
                                    checked={formData.all || false}
                                    disabled={true}
                                />
                                <span>{formConfig.checkboxGroup.title}</span>
                            </label>
                            {formConfig.checkboxGroup.checkboxes.map(checkbox => (
                                <label key={checkbox.id} className={styles.CollectionForm__checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name={checkbox.id}
                                        checked={formData[checkbox.id] || false}
                                        disabled={true}
                                    />
                                    <div>
                                        {checkbox.label.split(checkbox.link?.text || '').map((part, index) => (
                                            <span key={index}>
                                                {part}
                                                {index < checkbox.label.split(checkbox.link?.text || '').length - 1 && checkbox.link && (
                                                    <span 
                                                        className={styles.CollectionForm__link}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            window.open(checkbox.link.url, '_blank', 'noreferrer,noopener');
                                                        }}
                                                    >
                                                        {checkbox.link.text}
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </form>
            </div>
        </div>
    )
}